/**
 * Multi-cluster Typesense configuration.
 *
 * The primary cluster (Typesense Cloud today) is configured via the
 * `TYPESENSE_*` env vars. Additional clusters — currently the self-hosted
 * cluster (see `infra/pulumi/typesense-self-hosted/`) — are included when
 * their env vars are present, so single-cluster environments (local, staging)
 * resolve to just the primary. Schema management and (eventually) the write
 * path iterate this list, making it the single source of truth for "which
 * clusters must hold our collections".
 */

export interface TypesenseClusterSpec {
    /** Stable label used in logs/metrics (e.g. "cloud", "self-hosted"). */
    name: string;
    host: string;
    port: number;
    protocol: string;
    apiKey: string;
}

function parsePort(raw: string, varName: string): number {
    const port = parseInt(raw, 10);
    if (isNaN(port)) {
        throw new Error(`${varName} must be a numeric port, got "${raw}"`);
    }
    return port;
}

export function resolveTypesenseClusters(env: {[key: string]: string | undefined}): TypesenseClusterSpec[] {
    const {TYPESENSE_HOST, TYPESENSE_PORT, TYPESENSE_PROTOCOL, TYPESENSE_API_KEY} = env;
    if (!TYPESENSE_HOST || !TYPESENSE_PORT || !TYPESENSE_PROTOCOL || !TYPESENSE_API_KEY) {
        throw new Error("Primary Typesense cluster env vars (TYPESENSE_HOST/PORT/PROTOCOL/API_KEY) are not set");
    }

    const clusters: TypesenseClusterSpec[] = [
        {
            name: "cloud",
            host: TYPESENSE_HOST,
            port: parsePort(TYPESENSE_PORT, "TYPESENSE_PORT"),
            protocol: TYPESENSE_PROTOCOL,
            apiKey: TYPESENSE_API_KEY,
        },
    ];

    const {TYPESENSE_SELF_HOSTED_HOST, TYPESENSE_SELF_HOSTED_API_KEY} = env;
    if (TYPESENSE_SELF_HOSTED_HOST && TYPESENSE_SELF_HOSTED_API_KEY) {
        clusters.push({
            name: "self-hosted",
            host: TYPESENSE_SELF_HOSTED_HOST,
            port: parsePort(env["TYPESENSE_SELF_HOSTED_PORT"] ?? "443", "TYPESENSE_SELF_HOSTED_PORT"),
            protocol: env["TYPESENSE_SELF_HOSTED_PROTOCOL"] ?? "https",
            apiKey: TYPESENSE_SELF_HOSTED_API_KEY,
        });
    }

    return clusters;
}
